/**
 * Variant dimension hooks.
 *
 * Dimensions come in two scopes: tenant-level library templates
 * (`product_id` null) and product-scoped dimensions. Attaching a library
 * dimension to a product copies it (copy-on-attach), so the product owns an
 * independent set of dimensions and values.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import { productKeys } from './useProducts';
import { productCatalogLayerKeys } from './productCatalogLayerKeys';
import type { ProductDimension, ProductDimensionValue } from '../types';

const BASE = '/api/v1/master-data/products';

export interface DimensionValueInput {
  value: string;
  sku_code?: string | null;
  position?: number;
}

export interface DimensionInput {
  name: string;
  position?: number;
  values?: DimensionValueInput[];
}

// ---------------------------------------------------------------- queries

export function useDimensionLibrary() {
  return useQuery({
    queryKey: productKeys.dimensionLibrary(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductDimension[] }>(
        `${BASE}/dimension-library`
      );
      return response.data.data;
    },
  });
}

export function useProductDimensions(productId: string) {
  return useQuery({
    queryKey: productKeys.dimensions(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductDimension[] }>(
        `${BASE}/products/${productId}/dimensions`
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

// --------------------------------------------------------------- helpers

/** Invalidate the dimension list a mutation touched (product-scoped or library). */
function useDimensionInvalidator() {
  const queryClient = useQueryClient();
  return (productId: string | null) => {
    if (productId) {
      queryClient.invalidateQueries({ queryKey: productKeys.dimensions(productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.variants(productId) });
      // The batched catalog-layers read holds this product's layers too, and it
      // does not share a key prefix with the narrow key above.
      queryClient.invalidateQueries({ queryKey: productCatalogLayerKeys.all });
    } else {
      queryClient.invalidateQueries({ queryKey: productKeys.dimensionLibrary() });
    }
  };
}

// -------------------------------------------------------------- mutations

export function useCreateLibraryDimension() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async (data: DimensionInput) => {
      const response = await axiosInstance.post<{ data: ProductDimension }>(
        `${BASE}/dimension-library`,
        data
      );
      return response.data.data;
    },
    onSuccess: () => invalidate(null),
  });
}

export function useCreateProductDimension() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: DimensionInput }) => {
      const response = await axiosInstance.post<{ data: ProductDimension }>(
        `${BASE}/products/${productId}/dimensions`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useAttachLibraryDimension() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({
      productId,
      sourceDimensionId,
    }: {
      productId: string;
      sourceDimensionId: string;
    }) => {
      const response = await axiosInstance.post<{ data: ProductDimension }>(
        `${BASE}/products/${productId}/dimensions/attach`,
        { source_dimension_id: sourceDimensionId }
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useUpdateDimension() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({
      dimensionId,
      data,
    }: {
      dimensionId: string;
      productId: string | null;
      data: { name?: string; position?: number };
    }) => {
      const response = await axiosInstance.put<{ data: ProductDimension }>(
        `${BASE}/dimensions/${dimensionId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useDeleteDimension() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({ dimensionId }: { dimensionId: string; productId: string | null }) => {
      await axiosInstance.delete(`${BASE}/dimensions/${dimensionId}`);
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useAddDimensionValue() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({
      dimensionId,
      data,
    }: {
      dimensionId: string;
      productId: string | null;
      data: DimensionValueInput;
    }) => {
      const response = await axiosInstance.post<{ data: ProductDimensionValue }>(
        `${BASE}/dimensions/${dimensionId}/values`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useUpdateDimensionValue() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({
      valueId,
      data,
    }: {
      valueId: string;
      productId: string | null;
      data: { value?: string; sku_code?: string; position?: number };
    }) => {
      const response = await axiosInstance.put<{ data: ProductDimensionValue }>(
        `${BASE}/dimension-values/${valueId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}

export function useDeleteDimensionValue() {
  const invalidate = useDimensionInvalidator();
  return useMutation({
    mutationFn: async ({ valueId }: { valueId: string; productId: string | null }) => {
      await axiosInstance.delete(`${BASE}/dimension-values/${valueId}`);
    },
    onSuccess: (_, { productId }) => invalidate(productId),
  });
}
